"use client";

import { useState, type ReactNode } from "react";
import type { BulkActionType } from "@/lib/emailCleaner/types";
import type { BulkProgress } from "@/lib/emailCleaner/useBulkActionWithProgress";

export function ScreenHeader({ title, onBack, action }: { title: string; onBack?: () => void; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:text-slate-300"
            aria-label="Back"
          >
            ←
          </button>
        )}
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h3>
      </div>
      {action}
    </div>
  );
}

export function ScanProgressBar({ stage, current, total }: { stage: string; current: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-6 text-center dark:border-slate-800 dark:bg-[#0f172a]/80">
      <p className="text-sm text-slate-600 dark:text-slate-400">{stage}</p>
      <div className="mx-auto mt-3 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Card({ children, onClick, className = "" }: { children: ReactNode; onClick?: () => void; className?: string }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`w-full rounded-2xl border border-slate-200/80 bg-white/80 p-4 text-left dark:border-slate-800 dark:bg-[#0f172a]/80 ${
        onClick ? "transition hover:border-violet-300 dark:hover:border-violet-500/50" : ""
      } ${className}`}
    >
      {children}
    </Comp>
  );
}

/** Live "Processed X of Y (Z%)" overlay shown while a bulk action runs —
 *  matches the app's runBulkActionWithProgress dialog. */
export function BulkActionProgressOverlay({ progress }: { progress: BulkProgress | null }) {
  if (!progress) return null;
  const pct = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl dark:bg-[#0f172a]">
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
          {progress.total === 0 ? "Preparing…" : `Processed ${progress.processed} of ${progress.total} (${pct}%)`}
        </p>
      </div>
    </div>
  );
}

const ACTION_MENU_OPTIONS: { type: BulkActionType; label: string; icon: string }[] = [
  { type: "markRead", label: "Mark Read", icon: "✓" },
  { type: "markUnread", label: "Mark Unread", icon: "●" },
  { type: "unstar", label: "Remove Star", icon: "☆" },
  { type: "star", label: "Star", icon: "★" },
  { type: "keepNewest", label: "Keep Newest", icon: "📥" },
  { type: "moveToLabel", label: "Move…", icon: "📁" },
  { type: "archive", label: "Archive", icon: "🗄" },
  { type: "trash", label: "Trash", icon: "🗑" },
];

/** "More actions" dropdown — matches the app's suggestion_actions_sheet
 *  (Mark Read/Unread, Star/Unstar, Keep Newest, Move to label, Archive, Trash).
 *  Renders as its own standalone button (never nest this inside another
 *  clickable Card/button — see the invalid-HTML bug this replaced). */
export function ActionsMenu({ onChoose }: { onChoose: (action: BulkActionType, labelName?: string) => void }) {
  const [open, setOpen] = useState(false);

  function choose(type: BulkActionType) {
    setOpen(false);
    if (type === "moveToLabel") {
      const name = window.prompt("Label name");
      if (name && name.trim()) onChoose("moveToLabel", name.trim());
      return;
    }
    onChoose(type);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:text-slate-400"
        aria-label="More actions"
      >
        ⌄
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-[#0f172a]">
            {ACTION_MENU_OPTIONS.map((o) => (
              <button
                key={o.type}
                type="button"
                onClick={() => choose(o.type)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <span>{o.label}</span>
                <span>{o.icon}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">{children}</p>;
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  danger,
  className = "",
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full px-5 py-2 text-sm font-semibold text-white transition disabled:opacity-60 ${
        danger ? "bg-red-600 hover:bg-red-500" : "bg-violet-600 hover:bg-violet-500"
      } ${className}`}
    >
      {children}
    </button>
  );
}
